import React, { useEffect, useState } from "react";
import Navbar from "../../components/Navbar/Navbar";
import Property from "../../components/Listing/Listing";
import Footer from "../../components/Footer/Footer";
import Loading from "../../components/Loading/Loading";
import styles from "./RentView.module.css";
import axios from "axios";
import { Grid } from "@mui/material";
import { Button } from "@mui/material";
import ListIcon from "@mui/icons-material/List";
import MapIcon from "@mui/icons-material/Map";
import PropertyFilter from "../../components/PropertyFilter/PropertyFilter";
import DeletePropertyModal from "../../components/dashbaord/dashboardUtil/DeletePropertyModal/DeletePropertyModal";
import PropertyMapView from "../../components/Map/PropertyAmazonMapView";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";

const RentView = () => {
  const { accessToken } = useAuth();

  const [allListings, setAllListings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState("map"); // Toggle between "list" and "map"
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [addressInputState, setAddressInputState] = useState("");

  // Convert an object to a query string
  const toQueryString = (params) => {
    return new URLSearchParams(params).toString();
  };

  const sortAndAlternateListings = (listings) => {
    // Sort listings by created_at
    const sortedListings = listings.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );

    const premiumProperties = sortedListings.filter(
      (listing) => listing.is_premium === true
    );
    const normalProperties = sortedListings.filter(
      (listing) => !(listing.is_premium === true)
    );

    // Alternate premium and normal properties
    const alternatedListings = [];
    let i = 0,
      j = 0;

    while (i < normalProperties.length || j < premiumProperties.length) {
      if (i < normalProperties.length) {
        alternatedListings.push(normalProperties[i]);
        i++;
      }
      if (j < premiumProperties.length) {
        alternatedListings.push(premiumProperties[j]);
        j++;
      }
    }

    return alternatedListings;
  };

  // Fetch listings data
  const fetchListingsData = async (filters = {}) => {
    try {
      const queryString = toQueryString(filters);
      setAddressInputState(filters.addressInput);
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/listings/rent?${queryString}`
      );

      if (response.status === 200 && response.data.success) {
        const listings = response.data.listings;

        if (accessToken) {
          const listingsWithFavorites = await fetchFavorites(listings);
          // console.log("listingsWithFavorites: ", listingsWithFavorites);

          const sortedListings = sortAndAlternateListings(
            listingsWithFavorites
          );
          const newSortedListings = [...sortedListings].sort((a, b) => {
            return (b.is_premium === true) - (a.is_premium === true);
          });

          setAllListings(newSortedListings);
        } else {
          // If not logged in, just display the listings
          const sortedListings = sortAndAlternateListings(listings);
          const newSortedListings = [...sortedListings].sort((a, b) => {
            return (b.is_premium === true) - (a.is_premium === true);
          });

          setAllListings(newSortedListings);
        }
        // const alternatedListings = sortAndAlternateListings(
        //   response.data.listings
        // );
        // setAllListings(alternatedListings);
        // console.log(alternatedListings);
      }
    } catch (error) {
      console.error("Error fetching listings:", error);
    }
  };

  const fetchFavorites = async (listings) => {
    try {
      const favoritesResponse = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/listings/favorites/`,
        {},
        {
          headers: { Authorization: accessToken },
        }
      );

      let favoriteListingIds = [];
      if (
        favoritesResponse.status === 200 &&
        favoritesResponse.data.success &&
        Array.isArray(favoritesResponse.data.favorites)
      ) {
        favoriteListingIds = favoritesResponse.data.favorites; // favorites is already an array of IDs
      }

      // Mark listings as favorite
      const listingsWithFavorites = listings.map((listing) => ({
        ...listing,
        isFavorite: favoriteListingIds.includes(listing.id),
      }));

      // console.log("Listings with favorites marked:", listingsWithFavorites);

      return listingsWithFavorites;
    } catch (error) {
      console.error("Error fetching favorites:", error);
      return listings; // Return original listings if there's an error
    }
  };

  // Open the delete modal
  const openDeleteModal = (listing) => {
    setSelectedListing(listing);
    setIsDeleteModalOpen(true);
  };

  // Close the delete modal
  const closeDeleteModal = () => {
    setSelectedListing(null);
    setIsDeleteModalOpen(false);
  };

  // Handle delete property function with reason
  const handleDeleteProperty = async (id, reason) => {
    try {
      const response = await axios.delete(`$${id}`, {
        data: { reason }, // Send reason in the delete request body
      });

      if (response.status === 200) {
        setAllListings((prevListings) =>
          prevListings.filter((listing) => listing.id !== id)
        );
        toast.success("Property deleted successfully.");
      } else {
        toast.error("Failed to delete property.");
      }
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting property:", error);
      closeDeleteModal();
    }
  };

  const handleApplyFilters = (filterData) => {
    fetchListingsData(filterData);
  };

  useEffect(() => {
    fetchListingsData();

    setIsLoading(false);
  }, []);

  const handleToggleChange = (event, newView) => {
    if (newView) {
      setViewMode(newView);
    }
  };

  return (
    <>
      <Navbar />
      <div className={styles.title}>
        <h1>Rent a new home!</h1>
      </div>

      {/* Toggle between List and Map */}
      <Grid container justifyContent="center" sx={{ marginBottom: "30px" }}>
        <div className={styles.toggleContainer}>
          {/* List View Button */}
          <Button
            variant={viewMode === "list" ? "contained" : "outlined"}
            onClick={() => setViewMode("list")}
            className={viewMode === "list" ? styles.listSelected : ""}
          >
            <ListIcon />
            List
          </Button>

          {/* Map View Button */}
          <Button
            variant={viewMode === "map" ? "contained" : "outlined"}
            onClick={() => setViewMode("map")}
            className={viewMode === "map" ? styles.mapSelected : ""}
          >
            <MapIcon />
            Map
          </Button>
        </div>
      </Grid>

      {isLoading ? (
        <div className={styles.buyAndSellLoading}>
          <Loading />
        </div>
      ) : (
        <>
          <PropertyFilter onApplyFilters={handleApplyFilters} />
          <div className={styles.viewContent}>
            {viewMode === "list" ? (
              <div className={styles.propertyList}>
                {allListings.map((listing, index) => (
                  <Property key={index} listing={listing} />
                ))}
              </div>
            ) : (
              <div className={styles.mapPlaceholder}>
                {allListings && allListings.length ? (
                  <PropertyMapView
                    listings={allListings}
                    addressInputState={addressInputState}
                  />
                ) : (
                  <div>There are no properties available in this area!</div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      <Footer />

      {/* Delete Property Modal */}
      {isDeleteModalOpen && (
        <DeletePropertyModal
          listing={selectedListing}
          onClose={closeDeleteModal}
          onDelete={handleDeleteProperty}
        />
      )}
    </>
  );
};

export default RentView;
